import create from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { v4 as uuidV4 } from "uuid";
import * as classroomApi from "@/js/api/classroomApi";
import * as classroomPolicyApi from "@/js/api/classroomPolicyApi";
import useStore from "stores/index";
import { datadogLogs } from "@datadog/browser-logs";
import { useSelectedClassroomStore } from "stores/v2/selectedClassroomStore";
import { useUserSessionStore } from "./userSessionStore";

interface AddOrModifyAppControlRequest extends classroomPolicyApi.NativeAppControlRule {
	classroomStableIdV1: string;
	classroomSourceName: string;
}

interface AddAppControlResponse {
	ids: string[];
	error?: unknown;
}

interface ModifyAppControlResponse {
	error?: unknown;
}

interface GetClassroomPolicyResponse {
	policy: classroomPolicyApi.ClassroomPolicy;
	appControls?: classroomPolicyApi.NativeAppControlRule[];
}

interface Reward {
	tag?: string; // If reward is for a signature
	website?: string; // If reward is for a website
}

interface Options {
	skipValidations?: boolean;
	callback?: (data: unknown) => void;
	onApiError?: (error: unknown) => void;
	onApiSuccess?: () => void;
}

interface PublishOptions {
	noFetch?: boolean;
	allowed?: classroomPolicyApi.FilteringRule[];
	blocked?: classroomPolicyApi.FilteringRule[];
	callback?: (data: unknown) => void;
	onApiError?: (data: unknown) => void;
	skipValidations?: boolean;
}

interface ClassroomPolicyState {
	nativeAppControls: classroomPolicyApi.NativeAppControlRule[];
	locked_users: string[]; // Usernames of paused students

	internet_blocked: boolean;
	internet_blocked_lifetime: number;
	internet_blocked_override: number;
	allowed: classroomPolicyApi.FilteringRule[];
	blocked: classroomPolicyApi.FilteringRule[];

	reward: Reward[];
	reward_active_until: number; // Epoch time
	bypass_active_until: number; // Epoch time

	loaded: boolean;
	chat_blocked: boolean;

	hasLoaded: () => boolean;
	isChatBlocked: () => boolean;
	isCurrentlyBlocked: (item: string, user?: string) => boolean;
	getRewardApps: () => string[];
	getRewardWebsites: () => string[];
	getRewardActiveUntil: () => number;

	/** Checks if *any* students are currently paused in the active class */
	isPauseActive: () => boolean;
	getPausedStudents: () => string[];
	setPausedStudents: (pausedStudents: string[]) => void;

	toggleChatBlocked: () => void;

	publishPolicyBlob: (options?: PublishOptions) => Promise<void>;
	fetchPolicyAndPopulateStore: (payload: { options?: Options }) => Promise<void>;
	updateStoreData: (data: { policy: classroomPolicyApi.ClassroomPolicy; appControls?: classroomPolicyApi.NativeAppControlRule[] }) => void;

	addAllowedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;
	modifyAllowedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;
	removeAllowedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;
	addBlockedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;
	modifyBlockedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;
	removeBlockedRule: (payload: classroomPolicyApi.FilteringRule, options?: Options) => void;

	addAppControl: (
		payload: {
			classroomId: string;
			classroomSourceName: string;
			classroomStableIdV1: string;
			data: classroomPolicyApi.NativeAppControlRule;
		},
		options?: Options
	) => Promise<void>;
	modifyAppControl: (
		payload: {
			classroomId: string;
			classroomSourceName: string;
			classroomStableIdV1: string;
			data: classroomPolicyApi.NativeAppControlRule;
		},
		options?: Options
	) => Promise<void>;
	removeAppControl: (
		payload: {
			classroomStableIdV1: string;
			classroomId: string;
			classroomSourceName: string;
			/** ID of the app to remove */
			id: string;
		},
		options?: Options
	) => Promise<void>;

	removeAllRulesAndAppControls: (payload: {
		classroomId: string;
		classroomStableIdV1: string;
		classroomSourceName: string;
		options?: Options;
	}) => Promise<void>;

	setReward: (reward: { apps: string[]; websites: string[]; active_until: number }) => void;
	setBypassActiveUntil: (activeUntil: number) => void;
}

export const useClassroomPolicyStore = create(
	subscribeWithSelector<ClassroomPolicyState>((set, get) => ({
		nativeAppControls: [],
		locked_users: [],

		internet_blocked: false,
		internet_blocked_lifetime: 0,
		internet_blocked_override: 0,
		allowed: [],
		blocked: [],

		reward: [],
		reward_active_until: 0,
		bypass_active_until: 0,

		loaded: false,
		chat_blocked: true,

		hasLoaded: () => {
			return get().loaded;
		},
		isChatBlocked: () => {
			return get().chat_blocked;
		},
		isCurrentlyBlocked: (item, user) => {
			const { blocked } = get();
			for (const blockedEntry of blocked) {
				// If the item is website or tag, and user is either matched or undefined
				if (
					(blockedEntry.website === item || blockedEntry.tag === item) &&
					(blockedEntry.user === undefined || blockedEntry.user === user)
				) {
					return true;
				}
			}
			return false;
		},
		getRewardApps: () => {
			const { reward } = get();
			const result: string[] = [];
			for (const r of reward) {
				if (r.tag) result.push(r.tag);
			}
			return result;
		},
		getRewardWebsites: () => {
			const { reward } = get();
			const result: string[] = [];
			for (const r of reward) {
				if (r.website) result.push(r.website);
			}
			return result;
		},
		getRewardActiveUntil: () => {
			return get().reward_active_until;
		},
		isPauseActive: () => {
			const { locked_users } = get();
			return locked_users.length > 0;
		},
		getPausedStudents: () => {
			return get().locked_users;
		},

		toggleChatBlocked: () => {
			set((state) => ({ chat_blocked: !state.chat_blocked }));
			setTimeout(() => {
				void get().publishPolicyBlob();
			}, 0);
		},

		publishPolicyBlob: async (options = {}) => {
			const activeClassroom = useStore.getState().classroom.getClassroomV2(useUserSessionStore.getState().getActiveGroup() as string);
			if (!activeClassroom) return;

			const name = activeClassroom.sourceId;
			const {
				internet_blocked,
				internet_blocked_lifetime,
				internet_blocked_override,
				allowed,
				blocked,
				locked_users,
				reward,
				reward_active_until,
				bypass_active_until,
				chat_blocked,
			} = get();

			const policy = {
				internet_blocked,
				internet_blocked_lifetime,
				internet_blocked_override,
				allowed: options.allowed ?? allowed,
				blocked: options.blocked ?? blocked,
				locked_users,
				reward,
				reward_active_until,
				bypass_active_until,
				name,
				chat_blocked,
				skipValidations: options.skipValidations,
			};

			try {
				const request = {
					classId: activeClassroom.id,
					policy_id: activeClassroom.stableIdV1,
					policy,
				};
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore: Ignoring for now
				const data = await classroomPolicyApi.setClassroomPolicy(request);

				// Sync updated state to activeZoomStore - it's still used by chat.
				useStore.getState().zoom.updateState(policy);

				if (!options.noFetch) {
					void get().fetchPolicyAndPopulateStore({ options: {} });
				}
				if (typeof options.callback === "function") {
					options.callback(data);
				}
			} catch (error) {
				console.error("Error while publishing policy blob:", error);
				if (typeof options.onApiError === "function") {
					options.onApiError({ networkError: true });
				}
			}
		},

		fetchPolicyAndPopulateStore: async ({ options = {} }) => {
			const activeClassroom = useStore.getState().classroom.getClassroomV2(useSelectedClassroomStore.getState().id as string);
			const ffAppControls = useStore.getState().featureToggles.isNativeAppControlsEnabled();

			if (!activeClassroom) {
				if (options.onApiError) options.onApiError(undefined);
				return;
			}

			const name = activeClassroom.sourceId;
			if (!name || !activeClassroom.stableIdV1) {
				const logLine = `failed fetching policy for classroom name "${
					useSelectedClassroomStore.getState().id as string
				}", sourceId "${name}", ID "${activeClassroom.stableIdV1}"`;
				datadogLogs.logger.error(logLine, {
					name: useUserSessionStore.getState().getActiveGroup(),
					source_id: name,
					id: activeClassroom.stableIdV1,
				});
				if (options.onApiError) options.onApiError(undefined);
				return;
			}

			try {
				const data: GetClassroomPolicyResponse = await classroomPolicyApi.getClassroomPolicy(
					name,
					activeClassroom.stableIdV1,
					ffAppControls
				);
				get().updateStoreData(data);
				if (options.onApiSuccess) options.onApiSuccess();
			} catch (error) {
				if (options.onApiError) options.onApiError(error);
			}
		},

		updateStoreData: (data) => {
			const nativeAppControls = data.appControls || [];
			const policy = data.policy;
			// Use a functional set to read the current state if needed
			set((state) => ({
				nativeAppControls,
				locked_users: policy.locked_users !== undefined ? policy.locked_users : [],

				internet_blocked: policy.internet_blocked,
				internet_blocked_lifetime: policy.internet_blocked_lifetime,
				internet_blocked_override: policy.internet_blocked_override,
				allowed: policy.allowed,
				blocked: policy.blocked,

				reward: policy.reward ?? [],
				reward_active_until: policy.reward_active_until ?? state.reward_active_until,
				bypass_active_until: policy.bypass_active_until ?? state.bypass_active_until,

				loaded: true,
				chat_blocked: policy.chat_blocked ?? false,
			}));

			// Sync updated state to activeZoomStore - it's still used by chat.
			const policyStoreState = get();
			useStore.getState().zoom.updateState(policyStoreState);
		},

		addAllowedRule: (rule, options = {}) => {
			rule.id = uuidV4();
			const newAllowed = [...get().allowed, rule];

			// We do the final store update in the callback after publish
			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					allowed: newAllowed,
					callback: (data = {}) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.error("Error while adding allowed rule:", data);
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore: Ignoring for now
						} else if (data.allowed) {
							// The backend may return a rule with an updated ID, etc.
							set((state) => ({
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore: Ignoring for now
								allowed: [...state.allowed, data.allowed[0]],
							}));
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
					skipValidations: options.skipValidations,
				});
			}, 0);
		},

		modifyAllowedRule: (modifiedRule, options = {}) => {
			const policyType = modifiedRule.type;

			const { allowed, blocked } = get();
			let newAllowedList = [...allowed];
			const newBlockedList = [...blocked];

			if (policyType === "blocked") {
				// Removing from allowed, pushing to blocked
				newAllowedList = allowed.filter((rule) => rule.id !== modifiedRule.id);
				newBlockedList.push(modifiedRule);
			} else {
				// Updating an existing allowed rule in place
				const i = newAllowedList.findIndex((rule) => rule.id === modifiedRule.id);
				if (i >= 0) newAllowedList[i] = modifiedRule;
			}

			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					allowed: newAllowedList,
					blocked: newBlockedList,
					callback: (data = {}) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.error("Error while editing allowed rule:", data);
						} else {
							// If needed, store validated info:

							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore: Ignoring for now
							modifiedRule.validated = data?.blocked?.[0]?.validated || data?.allowed?.[0]?.validated;

							// Update store state
							set({ allowed: newAllowedList, blocked: newBlockedList });
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
					skipValidations: options.skipValidations,
				});
			}, 0);
		},

		removeAllowedRule: (rule, options = {}) => {
			const newAllowed = get().allowed.filter((existing) => {
				// Compare by all relevant fields
				return !(rule.website === existing.website && rule.tag === existing.tag && rule.user === existing.user && rule.id === existing.id);
			});

			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					allowed: newAllowed,
					callback: (data) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.error("Error while deleting allowed rule:", data);
						} else {
							set({ allowed: newAllowed });
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
				});
			}, 0);
		},

		addBlockedRule: (blockedInstruction, options = {}) => {
			blockedInstruction.id = uuidV4();
			const newBlocked = [...get().blocked, blockedInstruction];

			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					blocked: newBlocked,
					callback: (data = {}) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.error("Error while adding blocked rule:", data);
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore: Ignoring for now
						} else if (data.blocked) {
							set((state) => ({
								// eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore: Ignoring for now
								blocked: [...state.blocked, data.blocked[0]],
							}));
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
					skipValidations: options.skipValidations,
				});
			}, 0);
		},

		modifyBlockedRule: (modifiedRule, options = {}) => {
			const policyType = modifiedRule.type;

			const newAllowedList = [...get().allowed];
			let newBlockedList = [...get().blocked];

			if (policyType === "allowed") {
				// Removing from blocked, pushing to allowed
				newBlockedList = newBlockedList.filter((rule) => rule.id !== modifiedRule.id);
				newAllowedList.push(modifiedRule);
			} else {
				// Updating a blocked rule in place
				const i = newBlockedList.findIndex((rule) => rule.id === modifiedRule.id);
				if (i >= 0) newBlockedList[i] = modifiedRule;
			}

			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					allowed: newAllowedList,
					blocked: newBlockedList,
					callback: (data = {}) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.log("Error while editing blocked rule:", data);
						} else {
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore: Ignoring for now
							modifiedRule.validated = data?.blocked?.[0]?.validated || data?.allowed?.[0]?.validated;
							set({ allowed: newAllowedList, blocked: newBlockedList });
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
					skipValidations: options.skipValidations,
				});
			}, 0);
		},

		removeBlockedRule: (blockedInstruction, options = {}) => {
			const newBlocked: classroomPolicyApi.FilteringRule[] = [];
			const { blocked } = get();

			// If user-based blocking
			if (blockedInstruction.website === undefined && blockedInstruction.tag === undefined && blockedInstruction.user !== undefined) {
				for (const existing of blocked) {
					if (existing.website === undefined && existing.tag === undefined && existing.user === blockedInstruction.user) {
						continue;
					}
					newBlocked.push(existing);
				}
			} else {
				// Normal website/tag-based blocking
				for (const existing of blocked) {
					if (
						blockedInstruction.website === existing.website &&
						blockedInstruction.tag === existing.tag &&
						blockedInstruction.user === existing.user &&
						blockedInstruction.id === existing.id
					) {
						continue;
					}
					newBlocked.push(existing);
				}
			}

			setTimeout(() => {
				void get().publishPolicyBlob({
					noFetch: true,
					blocked: newBlocked,
					callback: (data: unknown) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore: Ignoring for now
						if (data.error) {
							console.error("Error while deleting blocked rule:", data);
						} else {
							set({ blocked: newBlocked });
						}
						if (options.callback) options.callback(data);
					},
					onApiError: options.onApiError,
				});
			}, 0);
		},

		addAppControl: async (entry, options = {}) => {
			const requestPayload: AddOrModifyAppControlRequest = {
				...entry.data,
				classroomStableIdV1: entry.classroomStableIdV1,
				classroomSourceName: entry.classroomSourceName,
			};

			try {
				const resp: AddAppControlResponse = await classroomApi.Put(`classrooms/${entry.classroomId}/app-control`, requestPayload);
				if (!resp.error) {
					// server responds with newly inserted IDs
					entry.data.id = resp.ids[0];
					set((state) => ({
						nativeAppControls: [...state.nativeAppControls, entry.data],
					}));
				}
				if (options.callback) options.callback(resp);
			} catch (err) {
				console.error("api error saving app control:", err);
			}
		},

		modifyAppControl: async (entry, options = {}) => {
			// optimistic update
			let nativeAppControls = [...get().nativeAppControls];
			const index = nativeAppControls.findIndex((control) => control.id === entry.data.id);
			if (index < 0) return;

			const old = nativeAppControls[index];
			nativeAppControls[index] = entry.data;

			set({ nativeAppControls });

			const rollbackOptimisticUpdate = () => {
				nativeAppControls = [...get().nativeAppControls];
				nativeAppControls[index] = old;
				set({ nativeAppControls });
			};

			const requestPayload: AddOrModifyAppControlRequest = {
				...entry.data,
				classroomStableIdV1: entry.classroomStableIdV1,
				classroomSourceName: entry.classroomSourceName,
			};

			try {
				const resp: ModifyAppControlResponse = await classroomApi.Post(`classrooms/${entry.classroomId}/app-control`, requestPayload);
				if (resp.error) {
					rollbackOptimisticUpdate();
				}
				if (options.callback) options.callback(resp);
			} catch (err) {
				console.error("api error saving app control:", err);
				rollbackOptimisticUpdate();
			}
		},

		removeAppControl: async ({ id, classroomStableIdV1, classroomId, classroomSourceName }, options = {}) => {
			try {
				await classroomApi.Delete(
					`classrooms/${classroomId}/app-controls`,
					{
						classroomSourceName,
						classroomStableIdV1,
						ids: [id],
					},
					() => null,
					() => null
				);
			} catch (err) {
				console.error("api removing app control:", err);
				return;
			}

			set((state) => ({
				nativeAppControls: state.nativeAppControls.filter((x) => x.id !== id),
			}));

			if (options.callback) {
				options.callback(undefined);
			}
		},

		removeAllRulesAndAppControls: async ({ classroomId, classroomStableIdV1, classroomSourceName, options = {} }) => {
			if (get().nativeAppControls.length > 0) {
				try {
					await classroomApi.Delete(
						`classrooms/${classroomId}/app-controls`,
						{
							classroomSourceName,
							classroomStableIdV1,
							ids: get().nativeAppControls.map((x) => x.id),
						},
						() => null,
						() => null
					);
					set({ nativeAppControls: [] });
				} catch (err) {
					console.error("api removing app control:", err);
				}
			}

			// Now handle removing all allowed/blocked rules
			void get().publishPolicyBlob({
				allowed: [],
				blocked: [],
				callback: (data: unknown) => {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore: Ignoring for now
					if (data.error) {
						console.error("Error while deleting all rules:", data);
					} else {
						set({ allowed: [], blocked: [] });
					}
					if (options.callback) options.callback(data);
				},
				onApiError: options.onApiError,
			});
		},

		setPausedStudents: (pausedStudents) => {
			set({
				locked_users: pausedStudents || [],
			});
		},

		setReward: (reward) => {
			const rewardApps = [];
			for (const app of reward.apps) {
				rewardApps.push({ tag: app });
			}
			for (const website of reward.websites) {
				rewardApps.push({ website });
			}
			set({
				reward: rewardApps,
				reward_active_until: reward.active_until,
			});

			setTimeout(() => {
				void get().publishPolicyBlob();
			}, 0);
		},

		setBypassActiveUntil: (activeUntil) => {
			set({ bypass_active_until: activeUntil });
			setTimeout(() => {
				void get().publishPolicyBlob();
			}, 0);
		},
	}))
);
